import { Column, ColumnDef } from '@tanstack/react-table';
import { SourcingOpenings } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import InformationCircle from '@/assets/icons/information.svg';
import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { HireStats } from '@/components/ui/hire-stats';
import { OpeningStatus } from '@/components/ui/opening-status';
import { SortColumnButton } from '@/components/ui/sort-column-button';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useHireUrl } from '@/hooks/use-hire-url';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import { formatShortDate } from '@/utils/date-util';
import { getOpeningDetailsPath } from '@/utils/opening-details-path';

import { ActionTakenNotifier } from './action-taken-notifier';
import { FlashAlert } from './flash-alert';
import { Menu } from './menu';
import { useAutomationFlashMessages } from './use-automation-flash-alerts';

export function useDashboardColumns(
  defaultMetricsPeriod?: number | null,
  rows: SourcingOpenings[] = [],
): ColumnDef<SourcingOpenings>[] {
  const navigate = useNavigate();
  const { dashboardPath } = useNavigationPaths();
  const { openJobPostingForm } = useJobPostingContext();

  const { getAutomationFlashMessages } = useAutomationFlashMessages();

  const handleSort = (column: Column<SourcingOpenings>) => {
    column.toggleSorting(column.getIsSorted() === 'asc');
  };

  // The Duration column is left-pinned, so its width is fixed rather than
  // content-driven (see DataTable). Only widen it to make room for the
  // flash-alert icon when a row on the current page actually has one -
  // otherwise it leaves a wide, empty gutter next to the duration text.
  const hasDurationFlashAlert = rows.some(row =>
    row.flash_alert_messages.some(
      msg => msg.automation_type === 'hiring_goal_ended',
    ),
  );

  const handleTitleClick = (rowItem: SourcingOpenings) => {
    return navigate(
      getOpeningDetailsPath({
        dashboardPath,
        openingSlug: rowItem.slug,
        hasActiveHiringGoal: Boolean(rowItem.hiring_goal),
        defaultMetricsPeriod,
      }),
    );
  };

  const handlePostJobClick = (opening: SourcingOpenings) => {
    openJobPostingForm({
      openings: [
        {
          id: opening.external_id,
          title: opening.title,
          active: opening.active,
          isSourcingEnabled: opening.is_sourcing_funnel,
        },
      ],
    });
  };

  const { getHireATHiringGoalUrl } = useHireUrl();

  const metricsPeriodDays = defaultMetricsPeriod ?? 30;
  const hiresApplicantsTooltipContent =
    defaultMetricsPeriod === null ? (
      <FormattedMessage
        defaultMessage="Based on hiring goal period or lifetime metrics if no goal set"
        description="Tooltip on Hires or Applicants column: reporting window when org default is lifetime"
      />
    ) : (
      <FormattedMessage
        defaultMessage="Based on hiring goal period or last {days} days if no goal set"
        values={{ days: metricsPeriodDays }}
        description="Tooltip on Hires or Applicants column: reporting window vs hiring goal; {days} is org default when no goal"
      />
    );
  const durationTooltipContent =
    defaultMetricsPeriod === null ? (
      <FormattedMessage
        defaultMessage="For openings without hiring goals, metrics are shown for their lifetime by default. You can change this in Settings."
        description="Tooltip on Duration column: lifetime default when org metrics period is lifetime"
      />
    ) : (
      <FormattedMessage
        defaultMessage="For openings without hiring goals, metrics are shown for the last {days} days by default. You can change this in Settings."
        values={{ days: metricsPeriodDays }}
        description="Tooltip on Duration column: default metrics window; {days} matches org setting"
      />
    );

  const totalHiresTooltipContent = (
    <FormattedMessage
      defaultMessage="How hires is counted? {br}The Hires column shows total hires in the goal duration – everyone hired in this period, including hires from applicants sourced earlier. This matches the count in Hire."
      values={{
        br: (
          <>
            <br />
            <br />
          </>
        ),
      }}
      description="Tooltip on Total Hires column: explains that it counts all hires in the goal period, not just sourced ones"
    />
  );

  const sourcedHiresTooltipContent = (
    <FormattedMessage
      defaultMessage="Hires from new applicants sourced in this period."
      description="Tooltip on Hires column in Sourced This Period group: counts only hires originating from sourced applicants"
    />
  );

  return [
    {
      id: 'select',
      size: 24,
      meta: { narrowCell: true },
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ),
    },
    {
      accessorKey: 'menu',
      size: 20,
      meta: { narrowCell: true },
      header: () => null,
      cell: ({ row }) => {
        return (
          <Menu
            opening={row.original}
            onPostJob={() => handlePostJobClick(row.original)}
          />
        );
      },
    },
    {
      accessorKey: 'title',
      size: 240,
      meta: { narrowCell: true },
      header: ({ column }) => (
        <SortColumnButton
          onSort={() => handleSort(column)}
          sorted={column.getIsSorted()}
        >
          <FormattedMessage
            defaultMessage="Opening name"
            description="Dashboard table: job title column with link to opening"
          />
        </SortColumnButton>
      ),
      cell: ({ row }) => {
        return (
          <div className="flex w-full items-center gap-1 overflow-hidden">
            <OpeningStatus active={row.original.active} />
            <Button
              variant="link"
              onClick={() => handleTitleClick(row.original)}
              className="h-auto text-left whitespace-break-spaces"
            >
              {row.original.title}
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'performance_status_metrics',
      size: 120,
      meta: { narrowCell: true },
      header: () => (
        <FormattedMessage
          defaultMessage="Status"
          description="Dashboard row: on-track vs at-risk vs goal met for the hire period"
        />
      ),
      cell: ({ row }) => {
        const performanceStatusMetrics =
          row.original.performance_status_metrics;
        const { status, criteria } = performanceStatusMetrics;

        const actionTakenMetrics = row.original.at_risk_metrics;

        if (!status) return <span>-</span>;

        return (
          <div className="flex items-center gap-1">
            <PerformanceStatusPill
              status={status}
              criteria={criteria}
              expected={
                criteria === 'hire'
                  ? performanceStatusMetrics.expected_hires_count
                  : performanceStatusMetrics.expected_applicants_count
              }
              actual={
                criteria === 'hire'
                  ? row.original.hire_stats.actual.total
                  : row.original.applicants_stats.actual
              }
              startDate={
                row.original.hiring_goal?.start_date &&
                formatShortDate(row.original.hiring_goal?.start_date)
              }
              endDate={
                row.original.hiring_goal?.hire_by &&
                formatShortDate(row.original.hiring_goal?.hire_by)
              }
              duration={row.original.hiring_goal?.duration ?? undefined}
              manageHiringGoalLink={getHireATHiringGoalUrl(
                row.original.brand_slug ?? 'no_brand',
                row.original.location_id,
                row.original.external_id,
              )}
            />
            {actionTakenMetrics && (
              <ActionTakenNotifier metrics={actionTakenMetrics} />
            )}
          </div>
        );
      },
    },
    {
      id: 'hires-duration-group',
      header: () => (
        <Typography variant="caption" className="text-fx-neutral-900">
          <FormattedMessage
            defaultMessage="HIRES & DURATION"
            description="Column group header: hiring goal progress and timeline columns"
          />
        </Typography>
      ),
      meta: {
        groupBg: 'bg-fx-brand-50',
        cellBg: 'bg-[rgba(238,242,255,0.2)]',
      },
      columns: [
        {
          accessorKey: 'total_hires',
          size: 100,
          header: () => (
            <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
              <FormattedMessage
                defaultMessage="Total Hires"
                description="Column: total hires in the goal duration including sourced and non-sourced applicants"
              />
              <Tooltip content={totalHiresTooltipContent}>
                <img src={InformationCircle} alt="" className="size-3" />
              </Tooltip>
            </span>
          ),
          cell: ({ row }) => (
            <HireStats
              stats={{
                actual: row.original.hire_stats.actual.total,
                expected: row.original.hire_stats.expected,
              }}
              openingSlug={row.original.slug}
              hiringGoalUrl={
                row.original.hiring_goal
                  ? undefined
                  : getHireATHiringGoalUrl(
                      row.original.brand_slug ?? 'no_brand',
                      row.original.location_id,
                      row.original.external_id,
                    )
              }
            />
          ),
        },
        {
          accessorKey: 'total_duration',
          size: hasDurationFlashAlert ? 132 : 120,
          header: () => (
            <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
              <FormattedMessage
                defaultMessage="Duration"
                description="Column: time left in the hire-by window"
              />
              <Tooltip content={durationTooltipContent}>
                <img src={InformationCircle} alt="" className="size-3" />
              </Tooltip>
            </span>
          ),
          cell: ({ row }) => {
            const automationMessageObj = row.original.flash_alert_messages.find(
              msg => msg.automation_type === 'hiring_goal_ended',
            );
            const automationMessage = getAutomationFlashMessages(
              automationMessageObj,
              row.original.duration_stats,
            );

            const status =
              row.original.performance_status_metrics?.status ?? null;
            const showDurationWarning =
              row.original.hiring_goal_percentage >= 90 &&
              status !== 'goal_met';

            return (
              <div className="flex items-center gap-1">
                <Duration
                  stats={row.original.duration_stats}
                  brand={row.original.brand_slug ?? 'no_brand'}
                  location={row.original.location_id}
                  funnel={row.original.external_id}
                  showMetricsPeriod={!row.original.hiring_goal}
                  metricsPeriod={defaultMetricsPeriod}
                  showNewGoalButton={
                    Boolean(row.original.hiring_goal) &&
                    !row.original.hiring_goal?.is_current
                  }
                  hiringGoalAutomationEnabled={
                    !!automationMessageObj?.automation_type
                  }
                  showDurationWarning={showDurationWarning}
                />
                {automationMessage && (
                  <FlashAlert description={automationMessage.description} />
                )}
              </div>
            );
          },
        },
      ],
    },
    {
      id: 'sourced-period-group',
      header: () => (
        <Typography variant="caption" className="text-fx-blue">
          <FormattedMessage
            defaultMessage="SOURCED THIS PERIOD"
            description="Column group header: applicants and hires originating from sourcing in the current period"
          />
        </Typography>
      ),
      meta: {
        groupBg: 'bg-fx-blue-50',
        cellBg: 'bg-[rgba(231,243,255,0.3)]',
      },
      columns: [
        {
          accessorKey: 'applicants_stats',
          header: () => (
            <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
              <FormattedMessage
                defaultMessage="Applicants"
                description="Column: pipeline volume vs target applicants"
              />
              <Tooltip content={hiresApplicantsTooltipContent}>
                <img src={InformationCircle} alt="" className="size-3" />
              </Tooltip>
            </span>
          ),
          cell: ({ row }) => {
            const automationMessageObj = row.original.flash_alert_messages.find(
              msg => msg.automation_type === 'automated_as_goal_met',
            );
            const automationMessage =
              getAutomationFlashMessages(automationMessageObj);

            return (
              <div className="flex items-center gap-1">
                <HireStats
                  stats={row.original.applicants_stats}
                  openingSlug={row.original.slug}
                  showOnlyActual={
                    !row.original.hiring_goal?.is_current ||
                    !row.original.applicants_stats.expected
                  }
                />
                {automationMessage && (
                  <FlashAlert description={automationMessage.description} />
                )}
              </div>
            );
          },
        },
        {
          accessorKey: 'hire_stats',
          header: () => (
            <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
              <FormattedMessage
                defaultMessage="Hires"
                description="Column: hires sourced in this period (from sourced applicants only)"
              />
              <Tooltip content={sourcedHiresTooltipContent}>
                <img src={InformationCircle} alt="" className="size-3" />
              </Tooltip>
            </span>
          ),
          cell: ({ row }) => (
            <HireStats
              stats={{
                actual: row.original.hire_stats.actual.cohort,
                expected: row.original.hire_stats.expected,
              }}
              isHighlighted={row.original.has_cross_funnel_transitions}
              openingSlug={row.original.slug}
              showOnlyActual
            />
          ),
        },
        {
          accessorKey: 'conversion_stats',
          header: () => (
            <FormattedMessage
              defaultMessage="Conversion"
              description="Column: actual vs expected applicant-to-hire rate"
            />
          ),
          cell: ({ row }) => (
            <ConversionStats
              stats={row.original.conversion_stats}
              showOnlyActual={
                !row.original.hiring_goal?.is_current ||
                !row.original.conversion_stats.expected
              }
            />
          ),
        },
      ],
    },
    {
      id: 'spend-cost-group',
      header: () => (
        <Typography variant="caption" className="text-fx-amber">
          <FormattedMessage
            defaultMessage="SPEND & COST"
            description="Column group header: budget spend and cost efficiency metrics"
          />
        </Typography>
      ),
      meta: {
        groupBg: 'bg-fx-amber-50',
        cellBg: 'bg-[rgba(255,248,225,0.2)]',
      },
      columns: [
        {
          accessorKey: 'budget_stats',
          header: () => (
            <FormattedMessage
              defaultMessage="Spend / Budget"
              description="Campaign spend and budget metrics"
            />
          ),
          cell: ({ row }) => {
            const automationMessageObj = row.original.flash_alert_messages.find(
              msg => msg.automation_type === 'target_budget_met',
            );
            const automationMessage =
              getAutomationFlashMessages(automationMessageObj);

            return (
              <div className="flex items-center gap-1">
                <CurrencyStats
                  stats={row.original.budget_stats}
                  isHighlighted={row.original.has_cross_funnel_spend}
                  openingSlug={row.original.slug}
                />
                {automationMessage && (
                  <FlashAlert description={automationMessage.description} />
                )}
              </div>
            );
          },
        },
        {
          accessorKey: 'total_cpa',
          header: () => (
            <FormattedMessage
              defaultMessage="CPA"
              description="Column: cost per applicant (acronym kept short for layout)"
            />
          ),
          cell: ({ row }) => (
            <CurrencyStats stats={row.original.cpa_stats} showOnlyActual />
          ),
        },
        {
          accessorKey: 'total_cph',
          header: () => (
            <FormattedMessage
              defaultMessage="CPH"
              description="Column: cost per hire (acronym kept short for layout)"
            />
          ),
          cell: ({ row }) => (
            <CurrencyStats stats={row.original.cph_stats} showOnlyActual />
          ),
        },
      ],
    },
    {
      accessorKey: 'channels',
      header: () => (
        <FormattedMessage
          defaultMessage="Channels"
          description="Column: which paid boards this opening is on (icons only)"
        />
      ),
      cell: ({ row }) => {
        const { channels } = row.original;

        if (_isEmpty(channels)) return <span>-</span>;

        const visibleChannels = channels?.slice(0, 5);
        const overflow =
          (channels?.length ?? 0) - (visibleChannels?.length ?? 0);

        return (
          <div className="flex w-max items-center">
            {visibleChannels?.map(channel => (
              <ChannelIcon
                key={channel.slug}
                variant="small"
                channelSlug={channel.slug}
                channelName={channel.name}
                logoSquareUrl={channel.logo_square}
                logoRectangleUrl={channel.logo_rectangle}
                className="-mr-1 last:mr-0"
                isRounded={true}
              />
            ))}
            {overflow > 0 && (
              <span className="ml-2 text-xs text-gray-500">+{overflow}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'sourcing_status',
      header: () => (
        <FormattedMessage
          defaultMessage="Sourcing"
          description="Column: org-wide sourcing status (on/off, automation, etc.)"
        />
      ),
      cell: ({ row }) => {
        const sourcingStatus = row.original.sourcing_status;

        if (!sourcingStatus) return <span>-</span>;

        return <SourcingStatusPill status={sourcingStatus} />;
      },
    },
    {
      accessorKey: 'brand_name',
      header: () => (
        <FormattedMessage
          defaultMessage="Brand"
          description="Column: employer brand for the requisition"
        />
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.brand_name}</Typography>
      ),
    },
  ];
}
